import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { useDirection } from '../../../contexts/DirectionContext';
import { Icon } from '@iconify/react';
import { Topic } from '../../../types/topic';
import { VideoContent } from '../../../types/content';
import { MediaSection } from '../../media/MediaSection';

interface TopicExplorerProps {
  topics: Topic[];
  onTopicSelect: (topicIds: string[]) => void;
  selectedTopics: string[];
  topicSections: Array<{
    id: string;
    title: string;
    videos: VideoContent[];
    loading: boolean;
    error: string | null;
  }>;
  className?: string;
}

const TOPICS_PER_PAGE = 12;
const MIN_TOPICS = TOPICS_PER_PAGE; // Initial number of topics to show

const TopicExplorer: React.FC<TopicExplorerProps> = ({
  topics,
  onTopicSelect,
  selectedTopics,
  topicSections,
  className = '',
}) => {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { isRTL } = useDirection();
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredTopics, setFilteredTopics] = useState(topics);
  const [selectedSection, setSelectedSection] = useState<'trending' | 'all' | null>(null);
  const [selectedTopicId, setSelectedTopicId] = useState<string | null>(null);
  const [visibleTopics, setVisibleTopics] = useState(TOPICS_PER_PAGE);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    setFilteredTopics(
      topics.filter(topic =>
        topic.title.toLowerCase().includes(searchQuery.toLowerCase())
      )
    );
    setVisibleTopics(TOPICS_PER_PAGE);
  }, [topics, searchQuery]);

  useEffect(() => {
    if (topics.length > 0 && selectedTopics.length === 0) {
      const isMobile = window.innerWidth < 640; // sm breakpoint in Tailwind
      if (!isMobile) {
        const popularTopic = [...topics].sort((a, b) => b.count - a.count)[0];
        onTopicSelect([popularTopic.id]);
      }
    }
  }, [topics, selectedTopics.length, onTopicSelect]);

  // Set selected topic ID when selectedTopics changes
  useEffect(() => {
    if (selectedTopics.length > 0) {
      setSelectedTopicId(selectedTopics[0]);
      setSelectedSection('all');
    }
  }, [selectedTopics]);

  // Organize topics into categories
  const categorizedTopics = [...topics].sort((a, b) => b.count - a.count).reduce((acc, topic) => {
    if (topic.count >= 200) {
      acc.featured.push(topic);
    } else if (topic.count >= 100) {
      acc.popular.push(topic);
    } else {
      acc.all.push(topic);
    }
    return acc;
  }, { featured: [], popular: [], all: [] } as { featured: Topic[], popular: Topic[], all: Topic[] });

  const handleTopicClick = (topic: Topic, section: 'trending' | 'all', isSelected: boolean) => {
    if (isSelected) {
      // Navigate to search page with topic
      window.location.href = `/search?topic=${topic.id}`;
      return;
    }

    if (selectedTopicId === topic.id && selectedSection === section) {
      setSelectedTopicId(null);
      setSelectedSection(null);
      onTopicSelect([]);
    } else {
      setSelectedTopicId(topic.id);
      setSelectedSection(section);
      onTopicSelect([topic.id]);
    }
  };

  const TopicCard = ({ 
    topic, 
    size = 'default',
    section
  }: { 
    topic: Topic; 
    size?: 'large' | 'default';
    section: 'trending' | 'all';
  }) => {
    const isSelected = selectedTopicId === topic.id && selectedSection === section;
    const matchingSection = isSelected ? topicSections.find(s => s.title === topic.title) : null;
    
    return (
      <div className={`
        flex flex-col w-full
        ${isSelected ? 'relative z-10' : ''}
      `}>
        <button
          onClick={() => handleTopicClick(topic, section, isSelected)}
          className={`
            flex items-center ${!isSelected ? 'justify-between' : ''}
            w-full h-full px-4 py-2.5
            rounded-xl
            transition-all duration-200 ease-in-out
            ${size === 'large' ? 'text-base' : 'text-sm'}
            ${isSelected
              ? 'bg-primary-500 text-white shadow-lg ring-4 ring-primary-500/20'
              : `${theme.mode === 'dark'
                  ? 'bg-surface-primary hover:bg-primary-500/5 hover:text-primary-500'
                  : 'bg-[rgba(var(--colors-primary-500-rgb),0.06)] hover:bg-primary-500/5 hover:text-primary-500'
                }`
            }
          `}
        >
          {isSelected ? (
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center gap-2">
                <span className={`font-medium truncate ${size === 'large' ? 'text-base' : 'text-sm'}`}>
                  {topic.title}
                </span>
                <span className="shrink-0 text-xs px-2 py-0.5 rounded-full bg-white/20">
                  {topic.count}
                </span>
              </div>
              <Icon 
                icon="ph:arrow-right-thin" 
                className={`
                  w-5 h-5
                  ${isRTL ? 'rotate-180' : ''}
                  opacity-80 group-hover:opacity-100
                  transition-opacity
                `}
              />
            </div>
          ) : (
            <>
              <span className={`font-medium truncate text-text-primary ${size === 'large' ? 'text-base' : 'text-sm'}`}>
                {topic.title}
              </span>
              <span className={`
                shrink-0
                text-xs px-2 py-0.5 rounded-full
                ${theme.mode === 'dark'
                  ? 'bg-surface-primary/20 text-text-primary'
                  : 'bg-surface-primary/25 text-text-primary'
                }
              `}>
                {topic.count}
              </span>
            </>
          )}
        </button>
        
        {/* Selected Topic Preview - Mobile Only */}
        {isSelected && matchingSection && (
          <div className="sm:hidden mt-2 relative -mx-4">
            <MediaSection
              key={matchingSection.id}
              items={matchingSection.videos}
              type="video"
              loading={matchingSection.loading}
              error={matchingSection.error}
              title=""
            />
          </div>
        )}
      </div>
    );
  };

  const handleToggleTopics = () => {
    if (visibleTopics > MIN_TOPICS) {
      setVisibleTopics(MIN_TOPICS);
      setIsExpanded(false);
      // Smooth scroll back to topics section
      const topicsSection = document.getElementById('topics-section');
      if (topicsSection) {
        topicsSection.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      setVisibleTopics(filteredTopics.length);
      setIsExpanded(true);
    }
  };

  return (
    <div className={className}>
      <div className="space-y-4 sm:space-y-8">
        {/* Featured and Popular Topics */}
        {/* <div>
          <div className="px-4 sm:px-6">
            <h2 className="text-xl font-semibold text-text-primary mb-4">
              {t('topics.trending')}
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {[...categorizedTopics.featured, ...categorizedTopics.popular]
                .slice(0, 6)
                .map(topic => (
                  <TopicCard 
                    key={topic.id} 
                    topic={topic} 
                    size="large"
                    section="trending"
                  />
                ))}
            </div>
          </div>
        </div> */}

        {/* All Topics with Filter */}
        <div>
          <div id="topics-section" className="px-2 sm:px-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4 mb-3 sm:mb-4">
              <h2 className="text-lg sm:text-xl font-semibold text-text-primary">
                {t('topics.all')}
              </h2>
              <div className="relative w-full sm:w-64">
                <Icon 
                  icon="ph:magnifying-glass-thin" 
                  className={`
                    absolute top-1/2 -translate-y-1/2 w-4 h-4 text-text-secondary
                    ${isRTL ? 'right-4' : 'left-4'}
                  `}
                />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search.filter')}
                  className={`
                    w-full py-2.5 text-sm
                    ${isRTL ? 'pl-4 pr-10' : 'pl-10 pr-4'}
                    rounded-full
                    text-text-primary
                    placeholder:text-text-secondary/50
                    transition-all duration-200
                    focus:outline-none focus:ring-2
                    ${theme.mode === 'dark' ? 'bg-transparent border border-primary-700' : 'bg-surface-primary/10'}
                    focus:bg-surface-primary/15
                    focus:ring-primary-500/20
                  `}
                />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {filteredTopics
                .slice(0, visibleTopics)
                .map(topic => (
                  <TopicCard 
                    key={topic.id} 
                    topic={topic}
                    section="all"
                  />
                ))}
            </div>

            {filteredTopics.length > MIN_TOPICS && (
              <button
                onClick={handleToggleTopics}
                className={`
                  group
                  w-full mt-6 py-3
                  rounded-xl
                  text-sm font-medium
                  transition-all duration-200
                  ${theme.mode === 'dark'
                    ? 'bg-surface-primary/10 hover:bg-surface-primary/15'
                    : 'bg-surface-primary/15 hover:bg-surface-primary/20'
                  }
                  text-primary-500 hover:text-primary-600
                  flex items-center justify-center gap-2
                `}
              >
                <span>
                  {isExpanded 
                    ? t('topics.showLess')
                    : `${t('topics.showMore')} (${filteredTopics.length - visibleTopics})`
                  }
                </span>
                <Icon 
                  icon={isExpanded ? "ph:caret-up" : "ph:caret-down"} 
                  className="w-4 h-4 transition-transform duration-200 group-hover:translate-y-0.5"
                />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Desktop-only bottom slider */}
      {selectedTopicId && (
        <div className="hidden sm:block mt-4">
          <div className={`
            ${theme.mode === 'dark'
              ? 'bg-surface-primary/[0.02]'
              : 'bg-surface-primary/[0.03]'
            }
            rounded-xl
            backdrop-blur-sm
          `}>
            {topicSections
              .filter(section => section.title === filteredTopics.find(t => t.id === selectedTopicId)?.title)
              .map(section => (
                <MediaSection
                  key={section.id}
                  items={section.videos}
                  type="video"
                  loading={section.loading}
                  error={section.error}
                  title=""
                />
              ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TopicExplorer;